import { apiClient } from "@base/api/client";
import { useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";
import { z } from "zod";

const workItemSchema = z.object({
  contractor_id: z.number(),
  contractor_name: z.string(),
  trade_id: z.number(),
  trade_name: z.string(),
  quantity: z.number(),
});

const workCategorySchema = z.object({
  total_quantity: z.number(),
  items: z.array(workItemSchema),
});

const dailySummaryResponseSchema = z.object({
  date: z.string(),
  workforce: workCategorySchema,
  equipment: workCategorySchema,
});

export type WorkItem = z.infer<typeof workItemSchema>;
export type WorkCategory = z.infer<typeof workCategorySchema>;
export type DailySummaryResponse = z.infer<typeof dailySummaryResponseSchema>;

async function fetchDailySummary(projectId: string, date: string): Promise<DailySummaryResponse> {
  const response = await apiClient.get<unknown>("/project-management/api/analytics/daily-summary", {
    params: { project_id: projectId, date },
  });
  return dailySummaryResponseSchema.parse(response.data);
}

export function dailySummaryQueryOptions(projectId: string, date: string) {
  return {
    queryKey: ["daily-summary", projectId, date] as const,
    queryFn: () => fetchDailySummary(projectId, date),
  };
}

export function useDailySummaryQuery(
  projectId: string,
  date: string,
  options?: Omit<UseQueryOptions<DailySummaryResponse>, "queryKey" | "queryFn">,
) {
  return useQuery({
    ...dailySummaryQueryOptions(projectId, date),
    enabled: !!projectId && !!date,
    ...options,
  });
}
